import { api } from "@/api/client";

// Mirrors EmailThreadSerializer, EmailRepliesController and
// EmailCommentsController.

export interface Address {
  email: string;
  name: string | null;
}

export interface ThreadAttachment {
  filename: string;
  mime_type: string | null;
  size: number | null;
  message_id: string;
  // The MIME part id: stable, unlike Gmail's attachment ids.
  part_id: string;
  url: string;
}

export interface ThreadMessage {
  id: string;
  date: string | null;
  from_address: string | null;
  from_name: string | null;
  reply_to: Address[];
  to: Address[];
  cc: Address[];
  subject: string | null;
  snippet: string | null;
  body_text: string | null;
  body_html: string | null;
  sent: boolean;
  attachments: ThreadAttachment[];
  portal_reply: { sent_by: string; sent_as: string } | null;
}

export interface EmailThread {
  thread_id: string;
  ap_inbox: string;
  messages: ThreadMessage[];
}

export async function fetchThread(emailId: number): Promise<EmailThread> {
  const { data } = await api.get<EmailThread>(`/emails/${emailId}/thread`);
  return data;
}

export type SendAs = "ap" | "me";

export interface ReplyInput {
  sentAs: SendAs;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  bodyHtml: string;
  inReplyTo: string;
  files: File[];
  threadAttachments: { message_id: string; part_id: string }[];
}

export interface SentReply {
  id: number;
  status: string;
  sent_as: string;
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string | null;
  attachment_names: string[];
}

export async function sendReply(emailId: number, input: ReplyInput): Promise<SentReply> {
  const form = new FormData();
  form.append("sent_as", input.sentAs);
  input.to.forEach((a) => form.append("to[]", a));
  input.cc.forEach((a) => form.append("cc[]", a));
  input.bcc.forEach((a) => form.append("bcc[]", a));
  form.append("subject", input.subject);
  form.append("body_html", input.bodyHtml);
  form.append("in_reply_to", input.inReplyTo);
  input.files.forEach((f) => form.append("files[]", f, f.name));
  input.threadAttachments.forEach((t) => form.append("thread_attachments[]", JSON.stringify(t)));
  const { data } = await api.post<SentReply>(`/emails/${emailId}/replies`, form);
  return data;
}

export interface EmailComment {
  id: number;
  body: string;
  author: string;
  author_email: string;
  inbound_email_id: number;
  notified: string[];
  created_at: string;
  mine: boolean;
  can_delete: boolean;
}

export interface CommentRecipient {
  id: number;
  name: string | null;
  email: string;
}

export async function fetchComments(emailId: number): Promise<EmailComment[]> {
  const { data } = await api.get<EmailComment[]>(`/emails/${emailId}/comments`);
  return data;
}

export async function postComment(emailId: number, body: string, notifyUserIds: number[]): Promise<EmailComment> {
  const { data } = await api.post<EmailComment>(`/emails/${emailId}/comments`, { body, notify_user_ids: notifyUserIds });
  return data;
}

export async function deleteComment(id: number): Promise<void> {
  await api.delete(`/email_comments/${id}`);
}

export async function fetchCommentRecipients(): Promise<CommentRecipient[]> {
  const { data } = await api.get<CommentRecipient[]>("/email_comments/recipients");
  return data;
}

export function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error ?? (error as Error)?.message ?? "Something went wrong";
}
